import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { toast } from "react-toastify"
import moment from "moment"
import { IConsultantModel, IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import { IIsBulkSelectionEnabledPropsModel } from "../../../../models"
import ConsultantsListingSearchBar from "./_ConsultantsListingSearchBar"
import ConsultantsListingFilters from "./_ConsultantsListingFilters"
import ConsultantsListingPreview from "./_ConsultantsListingPreview"
import ConsultantListingList from "./_ConsultantsListingList"
import MyModal from "@common-utils/MyModal"
import ConsultantSuiviSelectEvent from "../suivi/_ConsultantSuiviSelectEvent"
import { IConsultantsListingContextPropsModel, IItemInPreviewModel } from "./core/_models"
import { IGetConsultantsListingRequestModel, IGetConsultantsListingResponseModel } from "../core/_models"
import { getConsultantsListingRequest } from "../core/_requests"

const REACT_QUERY_KEY_GET_LISTING_CONSULTANTS = "REACT_QUERY_KEY_GET_LISTING_CONSULTANTS"
const SESSION_STORAGE_KEY_CONSULTANTS_FILTERS = "CONSULTANTS_LISTING_FILTERS"

const ConsultantsListingContext = createContext<IConsultantsListingContextPropsModel>({} as IConsultantsListingContextPropsModel)
export const useConsultantsListingContext = () => useContext(ConsultantsListingContext)

const ConsultantsListing = () => {
     const queryClient = useQueryClient()

     // -- Refs for controlling form states (submitting, errors) --
     const filtersFormikRef = useRef<any>(null)
     const simpleSearchBarFormikRef = useRef<any>(null)
     const previewContainerRef = useRef<any>(null)

     // -- 1. State Initialization with Persistence --
     const [filters, setFilters] = useState<IGetConsultantsListingRequestModel>(() => {
          const savedFilters = sessionStorage.getItem(SESSION_STORAGE_KEY_CONSULTANTS_FILTERS)
          if (savedFilters) {
               try {
                    return JSON.parse(savedFilters)
               } catch (e) {
                    console.error("Error parsing saved consultants filters", e)
               }
          }
          // Default filters
          return {
               page: 1,
               pageLength: 20,
               dateFiltrage: moment().format("YYYY-MM-DD"), // Default is today
          }
     })

     const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false)
     const [itemInPreview, setItemInPreview] = useState<IItemInPreviewModel | null>(null)
     const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState<IIsBulkSelectionEnabledPropsModel>({
          enabled: false,
          checkedElementsInPage: [],
          areAllElementsInAllPagesChecked: false,
     })

     // Suivi Modal State
     const [selectedConsultantsForSuivi, setSelectedConsultantsForSuivi] = useState<{
          consultants: IConsultantModel[]
          modalSize: "lg" | "xl" | "fullscreen"
          modalNoPadding: boolean
          modalTitle?: JSX.Element
     }>({
          consultants: [],
          modalSize: "lg",
          modalNoPadding: false,
     })
     const [consultantsBeingUpdated, setConsultantsBeingUpdated] = useState<IConsultantModel[]>([])

     // -- 2. Persistence Effect --
     useEffect(() => {
          sessionStorage.setItem(SESSION_STORAGE_KEY_CONSULTANTS_FILTERS, JSON.stringify(filters))
     }, [filters])

     // -- 3. Main Query --
     // Critical: Added 'filters' to dependency array to force refetch on change
     const listingQuery = useQuery<IGetConsultantsListingResponseModel, AxiosError>(
          [REACT_QUERY_KEY_GET_LISTING_CONSULTANTS, filters],
          async () => {
               return getConsultantsListingRequest(filters)
                    .then(r => {
                         // Stop loaders on success
                         filtersFormikRef.current?.setSubmitting(false)
                         simpleSearchBarFormikRef.current?.setSubmitting(false)
                         return r.data
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data

                         // Handle validation errors
                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) filtersFormikRef.current?.setFieldError(key, error.errors[key])
                         }

                         // Stop loaders on error
                         filtersFormikRef.current?.setSubmitting(false)
                         simpleSearchBarFormikRef.current?.setSubmitting(false)

                         toast.error(error?.detail, { autoClose: false })
                         throw e
                    })
          },
          {
               keepPreviousData: true,
               refetchOnWindowFocus: false,
          }
     )

     // -- Handlers --
     function onSubmitSuiviCallback() {
          setConsultantsBeingUpdated(selectedConsultantsForSuivi.consultants)
          const ids = selectedConsultantsForSuivi.consultants.map(item => item.id as number)

          getConsultantsListingRequest({ ids, dateFiltrage: filters.dateFiltrage })
               .then(r => {
                    const not_returned_ids = ids.filter(id => !r.data.data.map(item => item.consultant.id).includes(id))
                    if (not_returned_ids.length > 0) {
                         listingQuery.refetch().then()
                    } else {
                         queryClient.setQueryData<IGetConsultantsListingResponseModel | undefined>(REACT_QUERY_KEY_GET_LISTING_CONSULTANTS, prev => {
                              if (prev) {
                                   // Update local cache
                                   prev.data = prev.data.map(item => {
                                        if (ids.includes(item.consultant.id as number)) {
                                             const updatedItem = r.data.data.find(updated => updated.consultant.id === item.consultant.id)
                                             if (updatedItem) return updatedItem
                                        }
                                        return item
                                   })
                                   // Remove items that are no longer in the list (e.g. transferred)
                                   const not_returned_ids = ids.filter(id => !r.data.data.map(item => item.consultant.id).includes(id))
                                   prev.data = prev.data.filter(item => !not_returned_ids.includes(item.consultant.id as number))
                              }
                              return prev
                         })
                         setItemInPreview(null)
                    }
               })
               .catch((e: AxiosError) => {
                    toast.error((e.response?.data as IHttpErrorResponseModel)?.detail, { autoClose: false })
               })
               .finally(() => {
                    setConsultantsBeingUpdated([])
                    setSelectedConsultantsForSuivi({ consultants: [], modalSize: "lg", modalNoPadding: false })
                    setIsBulkSelectionEnabled({ enabled: false, checkedElementsInPage: [], areAllElementsInAllPagesChecked: false })
                    toast.success("Votre action a bien été prise en compte.")
               })
     }

     return (
          <ConsultantsListingContext.Provider
               value={{
                    areFiltersVisible,
                    setAreFiltersVisible,
                    filters,
                    setFilters,
                    itemInPreview,
                    setItemInPreview,
                    REACT_QUERY_KEY_GET_LISTING_CONSULTANTS,
                    listingQuery,
                    filtersFormikRef,
                    previewContainerRef,
                    simpleSearchBarFormikRef,
                    isBulkSelectionEnabled,
                    setIsBulkSelectionEnabled,
                    selectedConsultantsForSuivi,
                    setSelectedConsultantsForSuivi,
                    consultantsBeingUpdated,
                    setConsultantsBeingUpdated,
               }}
          >
               {window.innerWidth >= 1200 ? <ViewXlOrHigher /> : <ViewLgOrLess />}

               {/* Modal for Suivi */}
               {selectedConsultantsForSuivi.consultants.length > 0 && (
                    <MyModal
                         title={selectedConsultantsForSuivi.modalTitle === undefined ? <>Choisissez un événement</> : selectedConsultantsForSuivi.modalTitle}
                         show={selectedConsultantsForSuivi.consultants.length > 0}
                         onCloseCallback={() => setSelectedConsultantsForSuivi({ consultants: [], modalSize: "lg", modalNoPadding: false })}
                         size={["lg", "xl"].includes(selectedConsultantsForSuivi.modalSize) ? (selectedConsultantsForSuivi.modalSize as "lg" | "xl") : "lg"}
                         fullscreen={selectedConsultantsForSuivi.modalSize === "fullscreen" ? true : undefined}
                         noPadding={selectedConsultantsForSuivi.modalNoPadding}
                    >
                         <ConsultantSuiviSelectEvent
                              consultants={selectedConsultantsForSuivi.consultants}
                              setModalSizeCallback={modalSize => setSelectedConsultantsForSuivi(prev => ({ ...prev, modalSize }))}
                              setModalNoPaddingCallback={val => setSelectedConsultantsForSuivi(prev => ({ ...prev, modalNoPadding: val }))}
                              setModalTitle={modalTitle => setSelectedConsultantsForSuivi(prev => ({ ...prev, modalTitle }))}
                              onSubmitCallback={onSubmitSuiviCallback}
                         />
                    </MyModal>
               )}
          </ConsultantsListingContext.Provider>
     )
}

const ViewLgOrLess = () => {
     const context = useConsultantsListingContext()
     return (
          <>
               <div className={`${context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <ConsultantsListingSearchBar />
               </div>
               {context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                    <div className={"mb-2"}>
                         <ConsultantsListingPreview />
                    </div>
               )}
               <div className={`${!context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <ConsultantsListingFilters />
               </div>
               <ConsultantListingList />
          </>
     )
}

const ViewXlOrHigher = () => {
     const context = useConsultantsListingContext()
     return (
          <div className={"row"}>
               <div className={`col-5 ${!context.areFiltersVisible ? "d-none" : ""}`}>
                    <ConsultantsListingFilters />
               </div>
               <div className={`col ${context.itemInPreview && context.itemInPreview.isPreviewVisible ? "" : "col-7"}`}>
                    <div className={"row"}>
                         <div className={context.itemInPreview && context.itemInPreview.isPreviewVisible ? "col-5" : ""}>
                              <div className={`mb-2 ${context.areFiltersVisible ? "d-none" : ""}`}>
                                   <ConsultantsListingSearchBar />
                              </div>
                              <ConsultantListingList />
                         </div>
                         {context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                              <div className="col-7">
                                   <ConsultantsListingPreview />
                              </div>
                         )}
                    </div>
               </div>
          </div>
     )
}

export default ConsultantsListing
